/**
 * Betting Controls Component
 * Fold / Call / Raise / All-in as 4 plain, equal-sized buttons — no card
 * styling, no floating timer ring. Deliberately small footprint since this
 * sits inside the RHS panel now, not as its own full-width section.
 */

import { useSecondsLeft } from '../../hooks/useSecondsLeft';
import React, { useState, useEffect } from 'react';
import { SPECIAL_CARD_INFO, SpecialCardType, type BlockedBettingMove } from '@pokercubed/shared';
import { formatChips } from '../../utils/format';

/** Which card took the move away, for the note under the buttons. */
const BLOCKING_CARD: Record<BlockedBettingMove, SpecialCardType> = {
  fold: SpecialCardType.ORIGAMI,
  checkCall: SpecialCardType.HELLRAISER,
  raise: SpecialCardType.COMMENTATOR,
};

/** One short line in the bar (the card's full effect is a long-press on it) — a longer one made the bar grow over the table. */
const BLOCKED_NOTE: Record<BlockedBettingMove, string> = {
  fold: "you can't fold this hand",
  checkCall: "you can't check or call this hand",
  raise: "you can't raise this hand",
};

interface BettingControlsProps {
  /** The table's bet level this round. */
  currentBet: number;
  /** What you've already put in this round. */
  yourBet: number;
  yourChips: number;
  /** Smallest raise increment on top of a call (or smallest opening bet). */
  minRaise: number;
  /** The move your special card rules out this hand (Origami/Hellraiser/Shepherd), if any. */
  blockedMove?: BlockedBettingMove | null;
  /** When the server acts for you if you don't (epoch ms), or null if no countdown is running. */
  turnDeadline: number | null;
  isYourTurn: boolean;
  isPending?: boolean; // Action in flight, disable buttons
  onFold: () => void;
  onCall: () => void;
  /** Called with the increment on top of the call (or the opening bet when nothing's been bet). */
  onRaise: (amount: number) => void;
  onAllIn: () => void;
}

// Four square, stand-alone buttons in a row — big and well apart, so a
// thumb can't easily hit the wrong one. The action on top, any amount under it.
const BUTTON_BASE =
  // Square on a big screen; on a phone a 60px-high button — big enough to hit, leaving room for the table.
  'aspect-square max-h-[88px] portrait:aspect-auto portrait:h-[60px] short:aspect-auto short:h-[52px] w-full flex flex-col items-center justify-center gap-0.5 rounded-xl shadow-lg text-sm sm:text-base font-bold leading-tight transition-colors touch-manipulation disabled:opacity-40 disabled:cursor-not-allowed';
const AMOUNT = 'block text-[11px] sm:text-xs font-semibold opacity-90';

const BettingControls: React.FC<BettingControlsProps> = ({
  currentBet,
  yourBet,
  yourChips,
  minRaise,
  blockedMove = null,
  turnDeadline,
  isYourTurn,
  isPending = false,
  onFold,
  onCall,
  onRaise,
  onAllIn,
}) => {
  const toCall = Math.max(0, currentBet - yourBet);
  const callAmount = Math.min(toCall, yourChips);
  const canCheck = toCall === 0;

  // The engine's raise amount is the increment on top of calling; the most
  // you can add is whatever's left after the call.
  const maxRaise = Math.max(0, yourChips - toCall);
  const effectiveMinRaise = Math.min(Math.max(1, minRaise), maxRaise);
  const canRaise = maxRaise > 0 && blockedMove !== 'raise';
  const canFold = blockedMove !== 'fold';
  const canCallOrCheck = blockedMove !== 'checkCall';
  // All-in is a raise when it's more than the call — Shepherd can't —
  // but always allowed for Hellraiser (it's how a short stack "raises").
  const canAllIn = yourChips > 0 && !(blockedMove === 'raise' && yourChips > toCall);
  const isOpeningBet = currentBet === 0;

  const [raiseAmount, setRaiseAmount] = useState(effectiveMinRaise);
  const [showRaiseSlider, setShowRaiseSlider] = useState(false);
  const secondsLeft = useSecondsLeft(isYourTurn ? turnDeadline : null);

  useEffect(() => {
    setRaiseAmount(effectiveMinRaise);
  }, [effectiveMinRaise]);

  // Close the slider whenever the turn passes.
  useEffect(() => {
    if (!isYourTurn) setShowRaiseSlider(false);
  }, [isYourTurn]);

  // Not your turn: the same four buttons, greyed out — so nothing moves when
  // your turn comes, and your finger's already in the right place.
  const waiting = !isYourTurn;

  const raiseTarget = isOpeningBet ? raiseAmount : currentBet + raiseAmount;
  const raiseVerb = isOpeningBet ? 'Bet' : 'Raise to';

  return (
    <div
      data-testid="betting-controls"
      data-your-turn={waiting ? 'false' : 'true'}
      className="p-2 sm:p-3 bg-gray-900/50 rounded-xl border border-gray-700"
    >
      {/* Chip balance + timer (or "waiting") — a small inline row, not a big floating ring */}
      <div className="flex items-center justify-between mb-1.5 px-0.5">
        <span className="text-xs font-bold text-yellow-400">{formatChips(yourChips)}</span>
        {waiting && <span className="text-xs text-gray-400">Waiting for your turn…</span>}
        {!waiting && secondsLeft !== null && (
          <span
            className={`text-xs font-bold ${secondsLeft <= 5 ? 'text-red-500' : 'text-yellow-500'}`}
          >
            {secondsLeft}s
          </span>
        )}
      </div>

      {/* 4 square buttons, left to right */}
      <div
        className={`grid grid-cols-4 gap-2.5 sm:gap-3 justify-items-center ${waiting ? 'grayscale opacity-60' : ''}`}
        aria-disabled={waiting}
      >
        <button
          onClick={onFold}
          disabled={waiting || !canFold || isPending}
          className={`${BUTTON_BASE} bg-red-700 hover:bg-red-600 text-white`}
        >
          Fold
        </button>
        <button
          onClick={onCall}
          disabled={waiting || !canCallOrCheck || isPending}
          className={`${BUTTON_BASE} bg-blue-700 hover:bg-blue-600 text-white`}
        >
          {canCheck ? (
            'Check'
          ) : (
            <>
              Call{' '}
              <span className={AMOUNT}>
                {formatChips(callAmount)}
                {callAmount < toCall ? ' (all-in)' : ''}
              </span>
            </>
          )}
        </button>
        <button
          onClick={() => setShowRaiseSlider(!showRaiseSlider)}
          disabled={waiting || !canRaise || isPending}
          className={`${BUTTON_BASE} bg-green-700 hover:bg-green-600 text-white`}
        >
          {isOpeningBet ? 'Bet' : 'Raise'}
        </button>
        <button
          onClick={onAllIn}
          disabled={waiting || !canAllIn || isPending}
          className={`${BUTTON_BASE} bg-orange-700 hover:bg-orange-600 text-white`}
        >
          All In <span className={AMOUNT}>{formatChips(yourChips)}</span>
        </button>
      </div>

      {blockedMove && (
        <div data-testid="blocked-move" className="mt-1.5 truncate text-center text-[10px] text-rose-300">
          {SPECIAL_CARD_INFO[BLOCKING_CARD[blockedMove]].name}: {BLOCKED_NOTE[blockedMove]}
        </div>
      )}

      {isPending && !waiting && (
        <div className="mt-1.5 text-center text-[10px] text-yellow-400">Sending…</div>
      )}

      {/* Raise slider — expands below the 4-button grid when Raise is tapped */}
      {showRaiseSlider && canRaise && !waiting && (
        <div className="mt-2 p-2 bg-gray-900/70 rounded-lg border border-gray-700 space-y-1.5">
          <div className="flex justify-between text-[10px] text-gray-400">
            <span>Min: {formatChips(isOpeningBet ? effectiveMinRaise : currentBet + effectiveMinRaise)}</span>
            <span>Max: {formatChips(isOpeningBet ? maxRaise : currentBet + maxRaise)}</span>
          </div>
          <input
            type="range"
            min={effectiveMinRaise}
            max={maxRaise}
            value={raiseAmount}
            onChange={(e) => setRaiseAmount(parseInt(e.target.value))}
            className="w-full h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer touch-manipulation"
          />
          <div className="grid grid-cols-3 gap-1">
            <button
              onClick={() => setRaiseAmount(effectiveMinRaise)}
              className="py-1 bg-gray-700 hover:bg-gray-600 text-white text-[10px] rounded touch-manipulation"
            >
              Min
            </button>
            <button
              onClick={() => setRaiseAmount(Math.floor((maxRaise - effectiveMinRaise) * 0.5 + effectiveMinRaise))}
              className="py-1 bg-gray-700 hover:bg-gray-600 text-white text-[10px] rounded touch-manipulation"
            >
              1/2
            </button>
            <button
              onClick={() => setRaiseAmount(maxRaise)}
              className="py-1 bg-gray-700 hover:bg-gray-600 text-white text-[10px] rounded touch-manipulation"
            >
              Max
            </button>
          </div>
          <button
            onClick={() => {
              onRaise(raiseAmount);
              setShowRaiseSlider(false);
            }}
            disabled={isPending}
            className={`w-full py-1.5 text-xs font-bold rounded-lg touch-manipulation ${
              isPending ? 'bg-gray-600 text-gray-400' : 'bg-green-700 hover:bg-green-600 text-white'
            }`}
          >
            {raiseVerb} {formatChips(raiseTarget)}
          </button>
        </div>
      )}
    </div>
  );
};

export default BettingControls;
